/**
 * Trinkets: cargo that is only worth what somebody will pay for it.
 *
 * Everything else in the hold has a use. A line goes in a wall, a reactor goes
 * in a compartment, a delivery goes to an address. A trinket does nothing at
 * all — and that is the point, because it means its price is not anchored to
 * anything. A station's opinion of a jar of Gloamly sand is the whole of what a
 * jar of Gloamly sand is worth.
 *
 * So this is the game's one *pure* trade good, and the loop is the oldest one
 * there is: find somewhere selling cheap, carry it somewhere buying dear, and
 * know the difference is worth the fuel.
 *
 * ── Prices are a fact about a station, and they do not move ─────────────────
 *
 * Each station's opinion of each trinket is drawn once from the world seed and
 * never changes. Not per restock, not per visit, not per day.
 *
 * That is deliberate and it is what makes the mechanic a *game* rather than a
 * slot machine: a route you found is a route that stays found, worth writing
 * down and worth flying again. A price that rerolled every two hours would make
 * scouting pointless, because nothing learned on Tuesday would be true on
 * Wednesday. The stock moves; the prices do not.
 *
 * ── Why trinkets have their own spread ──────────────────────────────────────
 *
 * The general trade markup is 1.2 and the general sell fraction at most 0.8, so
 * an ordinary round trip returns 67% — a deliberate, reliable loss, because
 * ordinary goods are not supposed to be an income.
 *
 * Applied to trinkets that spread swallows the mechanic whole. With prices
 * varying ±30%, a station pair only profits when the seller is cheaper than the
 * buyer by more than the spread — and at 67% that is **under six per cent of
 * pairs**. A player could scout a dozen stations and correctly conclude the
 * whole idea was broken.
 *
 * The figures below are the same arithmetic with a spread trinkets can actually
 * be traded through: about a fifth of pairs profit, the best pair returns half
 * again, and buying and selling in the *same* place still loses 18% — so there
 * is no standing-still money and never a reason to do anything but fly.
 */
import { Rng } from '../world/rng.js';
/** What a station adds when it sells you one. */
export const TRINKET_MARKUP = 1.1;
/** What a station pays of its own price when it buys one back. */
export const TRINKET_SELL = 0.9;
/**
 * How far a station's opinion may stray from the base price, either way.
 *
 * The whole mechanic lives in this number. Widen it and routes get more
 * lucrative and easier to find; narrow it far enough and no pair beats the
 * spread, whatever the spread is.
 */
export const TRINKET_SPREAD = 0.3;
/**
 * The table.
 *
 * Junk with a story attached, which is what makes carrying it across three
 * systems feel like something other than moving a number. The spread of base
 * prices matters more than any single figure: cheap and light ones are what a
 * starter hull can actually fill up on, and the dear ones are a reason to want a
 * bigger vault.
 *
 * Weights sit between 0.3 and 6 against the same scale a beacon (0.4) and a warp
 * core (4) use, so a hold of trinkets competes with real cargo for space rather
 * than riding along free.
 */
export const TRINKETS = [
    { id: 'trinket-0', name: 'Petrified Cheese Rind', weight: 0.8, base: 14 },
    { id: 'trinket-1', name: 'Jar of Gloamly Sand', weight: 1.2, base: 18 },
    { id: 'trinket-2', name: 'Cripzon Service Medal', weight: 0.3, base: 26 },
    { id: 'trinket-3', name: 'Meridian First-Hull Rivet', weight: 0.5, base: 31 },
    { id: 'trinket-4', name: 'Pressed Voidflower', weight: 0.3, base: 37 },
    { id: 'trinket-5', name: 'Gleag Shards', weight: 1.0, base: 44 },
    { id: 'trinket-6', name: 'Dead Rogue Transponder', weight: 2.2, base: 58 },
    { id: 'trinket-7', name: 'Strange Rock', weight: 3.4, base: 71 },
    { id: 'trinket-8', name: 'Gleep Module', weight: 3.0, base: 86 },
    { id: 'trinket-9', name: 'Alleged Cheddar Core Sample', weight: 2.8, base: 99 },
    { id: 'trinket-10', name: 'Introscope System', weight: 6.0, base: 128 },
    { id: 'trinket-11', name: 'Certified First Line', weight: 1.0, base: 164 },
];
const BY_ID = new Map(TRINKETS.map((one) => [one.id, one]));
export function trinketById(id) {
    return BY_ID.get(id);
}
/**
 * What this station thinks of this trinket, as a multiple of its base price.
 *
 * Between 0.7 and 1.3, drawn from the world seed and stable forever. Keyed on
 * the station *and* the trinket, so a station is not simply expensive across the
 * board — it is dear in some things and cheap in others, which is what makes a
 * shop worth reading rather than a single number worth remembering.
 *
 * One scale, used by both the buying and the selling price. **Not two.** Drawing
 * them separately would let a station roll cheap-to-buy and dear-to-sell at the
 * same time, which is a money printer with no travel in it — the exact thing the
 * spread exists to prevent.
 */
export function trinketScale(seed, stationId, trinketId) {
    const random = Rng.forEntity(seed, stationId, 'trinket', trinketId);
    return 1 - TRINKET_SPREAD + random.float() * TRINKET_SPREAD * 2;
}
/** What this station charges for one. */
export function trinketBuyPrice(seed, stationId, trinket) {
    return Math.max(1, Math.round(trinket.base * trinketScale(seed, stationId, trinket.id) * TRINKET_MARKUP));
}
/** What this station pays for one. */
export function trinketSellPrice(seed, stationId, trinket) {
    return Math.max(1, Math.round(trinket.base * trinketScale(seed, stationId, trinket.id) * TRINKET_SELL));
}
/**
 * How many kinds of trinket a station keeps, and which.
 *
 * Moderately rare: a handful out of a dozen, so no station is a catalogue and
 * finding the one that stocks what you want is part of the trade. Which ones is
 * drawn from the seed and fixed — the *quantities* move with the restock window,
 * like every other shelf, but what a station deals in does not.
 */
const MIN_KINDS = 2;
const MAX_KINDS = 5;
/**
 * What is on the shelf here, before anybody has bought any.
 *
 * Entirely fixed — the same trinkets, the same quantities, the same prices,
 * forever. Depletion is *not* modelled here: what has been bought this window is
 * tracked by the shelf, exactly as it is for lines and parts, and subtracted on
 * the way out. One restock mechanism for the whole station rather than a second
 * one that only trinkets use.
 *
 * Which means a route stays a route. A player who finds somewhere selling cheap
 * can come back to it, and the only thing that changes between visits is how
 * many are left.
 */
export function trinketStock(seed, stationId) {
    const which = Rng.forEntity(seed, stationId, 'trinket-stock');
    const kinds = which.int(MIN_KINDS, MAX_KINDS);
    /*
     * Chosen by shuffling a copy rather than by drawing until something new turns
     * up: the second version has no bound on how long it runs, and with a table
     * this small the difference is one allocation.
     */
    const pool = [...TRINKETS];
    for (let i = pool.length - 1; i > 0; i--) {
        const j = which.int(0, i);
        [pool[i], pool[j]] = [pool[j], pool[i]];
    }
    const chosen = pool.slice(0, kinds);
    return chosen.map((trinket) => {
        const amount = Rng.forEntity(seed, stationId, 'trinket-count', trinket.id);
        return {
            trinket,
            // Few of each. A trinket run is several kinds in small numbers rather than
            // a pallet of one thing, so hold space is spent on variety.
            quantity: amount.int(1, 4),
            price: trinketBuyPrice(seed, stationId, trinket),
        };
    });
}
//# sourceMappingURL=trinkets.js.map